/* Copyright (c) 2020-present CloudIO Inc */

import {
  Aggregate,
  DBRow,
  Filters,
  GenericDataRecord,
  getFilterDataType,
  SchemaMember,
  SchemaMemberValue,
} from '@cloudio-saas/datasource-types';
import stringify from 'json-stable-stringify';
import { ActionProps, Actions, ExpressionValue } from '../../componentregistry';
import {
  AttributeDataType,
  toAttributeDataType,
} from '../../componentregistry/components/SmartSearch';
import { Platform } from '../../componentregistry/platform/Platform';
import {
  getName,
  isFixedOperator,
  isMultiOperator,
  isNested,
  makeFilterEntryValid,
} from '../../components/SmartSearch/SearchUtils';
import IOError from '../../platform/IOError';
import Store, { QueryProps } from '../../store/Store';
import { isEmpty } from '../../utils/isEmpty';
import Logger from '../../utils/Logger';

const FIXED_DATE = new Date().toISOString();

const getValue = (
  val: string | number,
  dataType: AttributeDataType | undefined,
) => {
  switch (dataType) {
    case 'Number':
      return Number(val);
    case 'Boolean':
      return String(val).toLowerCase() === 'true';
    default:
      return val;
  }
};

export function getFilters<T>(
  itemId: string,
  appUid: string,
  pageId: string,
  platform: Platform,
  store: Store<T>,
  extraProps: Record<string, unknown>,
): Filters<T> {
  const { showEmptyFilterWarning } = extraProps;
  const filter: Filters<T> = [];
  const singleFilterChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetQueryFilter',
  ) as Actions.ActionSetQueryFilter.ActionSetQueryFilterItem[];

  const combinerFilterChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetFilterCombiner',
  ) as Actions.ActionSetFilterCombiner.ActionSetFilterCombinerItem[];

  for (let i = 0; i < singleFilterChildren.length; i++) {
    const set = singleFilterChildren[i];
    const { disableCondition, attribute, operator, hasTime } = set.props;
    const attr = store.getAttribute(attribute);
    if (!attr) {
      throw new IOError(
        'Developer Issue',
        `Missing attribute ${attribute} in data source ${store.ds}. Action Query on ${store.alias} aborted!`,
      );
    }
    let { value: exp } = set.props;
    if (exp && !Array.isArray(exp)) {
      exp = [exp as unknown as ExpressionValue];
    }
    if (
      platform.evalBoolean(
        appUid,
        pageId,
        set.itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: set.itemId },
        disableCondition,
      )
    ) {
      let value: unknown;
      const dataType = getFilterDataType(attr);
      if (operator === 'empty' || operator === 'notEmpty') {
        value = dataType === 'Boolean' ? true : '';
      } else if (operator === 'null' || operator === 'notNull') {
        value = 1;
      } else if (dataType === 'Date' && isFixedOperator(operator)) {
        // use a fixed date for fixed operators to avoid duplicate queries
        // this date is just a placeholder (of type date) and will not be used by the server
        value = FIXED_DATE;
      } else if ((operator === 'bn' || isMultiOperator(operator)) && dataType) {
        value = exp?.map((expr) =>
          evalExp(
            expr,
            toAttributeDataType(dataType),
            platform,
            extraProps,
            appUid,
            pageId,
            itemId,
          ),
        );
        if (value && typeof value === 'object' && operator !== 'bn') {
          const res: string = value.toString();
          if (res && res.includes(',')) {
            value = res.split(',');
          }
        }
      } else if (exp?.[0] && dataType) {
        value = evalExp(
          exp[0],
          toAttributeDataType(dataType),
          platform,
          extraProps,
          appUid,
          pageId,
          itemId,
        );
      } else {
        if (showEmptyFilterWarning) {
          platform.showMessage({
            msg: `Missing filter value for attribute [${attribute}] with operator [${operator}]. Hence ignored!! [Item: ${set.itemId}]`,
            type: 'warning',
            item: { appUid, pageId, itemId: set.itemId },
          });
        }
        // eslint-disable-next-line no-continue
        continue;
      }
      if (
        !isEmpty(value) ||
        (isEmpty(value) && (operator === 'empty' || operator === 'notEmpty'))
      ) {
        const entry =
          dataType === 'Date'
            ? {
                [attribute]: { [operator]: value },
                includesTime: hasTime ? 'Y' : 'N',
              }
            : { [attribute]: { [operator]: value } };

        const validEntry = makeFilterEntryValid(
          entry,
          store.getAttribute,
          true,
        );
        if (validEntry) {
          filter.push(validEntry);
          if (Logger.isLogEnabled()) {
            Logger.log({
              status: 'debug',
              itemType: set.type,
              itemId: set.itemId,
              msg: `Adding filter: ${stringify(validEntry)}`,
            });
          }
        } else {
          platform.showMessage({
            msg: `Invalid filter for attribute [${attribute}] with operator [${operator}] and value [${value}]. Hence ignored! [Item: ${set.itemId}]`,
            type: 'error',
            item: { appUid, pageId, itemId: set.itemId },
          });
        }
      } else if (showEmptyFilterWarning) {
        platform.showMessage({
          msg: `Missing filter value for attribute [${attribute}] with operator [${operator}]. Hence ignored! [Item: ${set.itemId}]`,
          type: 'warning',
          item: { appUid, pageId, itemId: set.itemId },
        });
      }
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: set.type,
        itemId: set.itemId,
        msg: `Skip adding filter as disableCondition [${disableCondition}] is false!`,
      });
    }
  }
  for (let i = 0; i < combinerFilterChildren.length; i++) {
    const cf = combinerFilterChildren[i];
    if (
      platform.evalBoolean(
        appUid,
        pageId,
        cf.itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: cf.itemId },
        cf.props.disableCondition,
      )
    ) {
      const cFilters = getFilters(
        cf.itemId,
        appUid,
        pageId,
        platform,
        store,
        extraProps,
      );
      if (cFilters.length) {
        const entry = { [cf.props.combiner]: cFilters };
        filter.push(entry);
        if (Logger.isLogEnabled()) {
          Logger.log({
            status: 'debug',
            itemType: cf.type,
            itemId: cf.itemId,
            msg: `Adding filter: ${stringify(entry)}`,
          });
        }
      }
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: cf.type,
        itemId: cf.itemId,
        msg: `Skip adding combiner filter as disableCondition [${cf.props.disableCondition}] is false!`,
      });
    }
  }
  return filter;
}

const evalExp = (
  exp: ExpressionValue,
  dataType: AttributeDataType,
  platform: Platform,
  extraProps: Record<string, unknown>,
  appUid: string,
  pageId: string,
  itemId: string,
) => {
  if (exp.type === 'expression') {
    let evalRes = platform.evalUnknown(
      appUid,
      pageId,
      itemId,
      'value',
      { ...extraProps, appUid, pageId, itemId },
      exp.value,
    );
    if (dataType === 'YN' && evalRes !== 'N') {
      evalRes = evalRes ? 'Y' : 'N';
    }
    if (dataType === 'TF' && evalRes !== 'F') {
      evalRes = evalRes ? 'T' : 'F';
    }
    return evalRes;
  }
  return getValue(exp.value, dataType);
};

export function getSimpleFilters<T>(
  itemId: string,
  appUid: string,
  pageId: string,
  platform: Platform,
  store: Store<T>,
  extraProps: Record<string, unknown>,
): SchemaMemberValue<T> {
  const { showEmptyFilterWarning } = extraProps;
  const data: SchemaMemberValue<T> = {};
  const simpleFilterChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetSimpleQueryFilter',
  ) as Actions.ActionSetSimpleQueryFilter.ActionSetSimpleQueryFilterItem[];

  for (let i = 0; i < simpleFilterChildren.length; i++) {
    const set = simpleFilterChildren[i];
    const { disableCondition, attribute, value: exp } = set.props;
    const attr = store.getAttribute(attribute);
    if (!attr) {
      throw new IOError(
        'Developer Issue',
        `Missing attribute ${attribute} in data source ${store.ds}. Action Query on ${store.alias} aborted!`,
      );
    }
    if (
      platform.evalBoolean(
        appUid,
        pageId,
        set.itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: set.itemId },
        disableCondition,
      )
    ) {
      const dataType = getFilterDataType(attr);
      const value = evalExp(
        exp,
        toAttributeDataType(dataType),
        platform,
        extraProps,
        appUid,
        pageId,
        itemId,
      ) as T[keyof T];
      if (!isEmpty(value)) {
        data[attribute as keyof T] = value;
      } else if (showEmptyFilterWarning) {
        platform.showMessage({
          msg: `Missing filter value for attribute [${attribute}]. Hence ignored! [Item: ${set.itemId}]`,
          type: 'warning',
          item: { appUid, pageId, itemId: set.itemId },
        });
      }
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: set.type,
        itemId: set.itemId,
        msg: `Skip adding filter as disableCondition [${disableCondition}] is false!`,
      });
    }
  }

  return data;
}

export function getFilterFromActions<T>(props: {
  appUid: string;
  extraProps: Record<string, unknown>;
  itemId: string;
  pageId: string;
  platform: Platform;
  store: Store<T>;
  showEmptyFilterWarning: boolean;
}): {
  aggregateList: Aggregate<T>[];
  data: SchemaMemberValue<T>;
  fetchDistinct: boolean;
  filter: Filters<T>;
  groupByAttributeCodeList: (keyof T)[];
  projection: SchemaMember<T, number>;
  sort: SchemaMember<T, number>;
} {
  const { appUid, platform, pageId, itemId, store, extraProps } = props;

  const filter = getFilters(
    itemId,
    appUid,
    pageId,
    platform,
    store,
    extraProps,
  );

  const data = getSimpleFilters(
    itemId,
    appUid,
    pageId,
    platform,
    store,
    extraProps,
  );

  const aggregateList: Aggregate<T>[] = [];
  const aggrChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetAggregateAttribute',
  ) as Actions.ActionSetAggregateAttribute.ActionSetAggregateAttributeItem[];
  aggrChildren.forEach((aggr) => {
    const attributeCode = aggr.props.attributeCode as keyof T;
    const intoAttributeCode =
      (aggr.props.intoAttributeCode as keyof T) || attributeCode;
    const { disableCondition, aggregateFunction } = aggr.props;
    if (!(attributeCode && aggregateFunction)) {
      platform.showMessage({
        msg: 'Missing attribute or aggregate function. Hence ignored!',
        type: 'warning',
        item: { appUid, pageId, itemId: aggr.itemId },
      });
    } else if (
      platform.evalBoolean(
        appUid,
        pageId,
        aggr.itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: aggr.itemId },
        disableCondition,
      )
    ) {
      const aggregate = { attributeCode, aggregateFunction, intoAttributeCode };
      aggregateList.push(aggregate);
      if (Logger.isLogEnabled()) {
        Logger.log({
          status: 'debug',
          itemType: aggr.type,
          itemId: aggr.itemId,
          msg: `Adding aggregate: ${stringify(aggregate)}`,
        });
      }
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: aggr.type,
        itemId: aggr.itemId,
        msg: `Skip adding aggregate as disableCondition [${disableCondition}] is false!`,
      });
    }
  });

  const groupByAttributeCodeList: (keyof T)[] = [];
  const grpChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetGroupByAttribute',
  ) as Actions.ActionSetGroupByAttribute.ActionSetGroupByAttributeItem[];
  grpChildren.forEach((grpBy) => {
    const { disableCondition } = grpBy.props;
    const attributeCode = grpBy.props.attributeCode as keyof T;
    if (!attributeCode) {
      platform.showMessage({
        msg: 'Missing attribute in ActionSetGroupByAttribute. Hence ignored!',
        type: 'warning',
        item: { appUid, pageId, itemId: grpBy.itemId },
      });
    } else if (
      platform.evalBoolean(
        appUid,
        pageId,
        itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: grpBy.itemId },
        disableCondition,
      )
    ) {
      groupByAttributeCodeList.push(attributeCode);
      if (Logger.isLogEnabled()) {
        Logger.log({
          status: 'debug',
          itemType: grpBy.type,
          itemId: grpBy.itemId,
          msg: `Adding groupBy: ${stringify(attributeCode)}`,
        });
      }
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: grpBy.type,
        itemId: grpBy.itemId,
        msg: `Skip adding groupBy as disableCondition [${disableCondition}] is false!`,
      });
    }
  });

  const sort: SchemaMember<DBRow<T>, number> = {};
  const sortChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetSort',
  ) as Actions.ActionSetSort.ActionSetSortItem[];
  sortChildren.forEach((sortChild, i) => {
    i++;
    const { disableCondition, sortOrder } = sortChild.props;
    const attributeCode = sortChild.props.attributeCode as keyof T;
    if (!attributeCode) {
      platform.showMessage({
        msg: 'Missing attribute in ActionSetSort. Hence ignored!',
        type: 'warning',
        item: { appUid, pageId, itemId: sortChild.itemId },
      });
    } else if (
      platform.evalBoolean(
        appUid,
        pageId,
        itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: sortChild.itemId },
        disableCondition,
      )
    ) {
      sort[attributeCode] = sortOrder === 'DESC' ? -i : i;
      if (Logger.isLogEnabled()) {
        Logger.log({
          status: 'debug',
          itemType: sortChild.type,
          itemId: sortChild.itemId,
          msg: `Adding sort: ${stringify({
            [attributeCode]: sortOrder === 'DESC' ? -i : i,
          })}`,
        });
      }
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: sortChild.type,
        itemId: sortChild.itemId,
        msg: `Skip adding sort as disableCondition [${disableCondition}] is false!`,
      });
    }
  });

  if (Object.keys(sort).length > 0) {
    store.setSort(sort);
  }

  const projection: SchemaMember<T, number> = {};
  const projChildren = platform.getChildrenItemsById(
    appUid,
    pageId,
    itemId,
    (child): boolean => child.type === 'ActionSetProjection',
  ) as Actions.ActionSetProjection.ActionSetProjectionItem[];
  projChildren.forEach((proj) => {
    const { disableCondition } = proj.props;
    const attributeCode = proj.props.attributeCode as keyof T;
    if (!attributeCode) {
      platform.showMessage({
        msg: 'Missing attribute in ActionSetProjection. Hence ignored!',
        type: 'warning',
        item: { appUid, pageId, itemId: proj.itemId },
      });
    } else if (
      platform.evalBoolean(
        appUid,
        pageId,
        itemId,
        'disableCondition',
        { ...extraProps, appUid, pageId, itemId: proj.itemId },
        disableCondition,
      )
    ) {
      projection[attributeCode] = 1;
    } else if (Logger.isLogEnabled()) {
      Logger.log({
        status: 'debug',
        itemType: proj.type,
        itemId: proj.itemId,
        msg: `Skip adding projection as disableCondition [${disableCondition}] is false!`,
      });
    }
  });

  const fetchDistinct =
    platform.getChildrenItemsById(
      appUid,
      pageId,
      itemId,
      (child): boolean => child.type === 'ActionSetDistinct',
    ).length > 0;

  if (fetchDistinct && Logger.isLogEnabled()) {
    Logger.log({
      status: 'debug',
      itemType: 'ActionSetDistinct',
      itemId,
      msg: 'Adding fetchDistinct: true',
    });
  }

  return {
    aggregateList,
    data,
    fetchDistinct,
    filter,
    groupByAttributeCodeList,
    projection,
    sort,
  };
}

export async function makeQueryProps(
  _props: ActionProps<'ActionQuery' | 'ActionSetDefaultFilters'> & {
    force?: boolean;
    showEmptyFilterWarning: boolean;
  },
): Promise<[Store<GenericDataRecord>, QueryProps<GenericDataRecord>]> {
  const {
    platform,
    embeddedPageId,
    appUid,
    pageId,
    itemId,
    alias,
    force,
    showEmptyFilterWarning,
  } = _props;
  if (isEmpty(alias)) {
    throw Error(
      `Missing dataSource alias for Action Query! [Page ID: ${pageId}, Item ID: ${itemId}]`,
    );
  }
  let storePageId = pageId;
  if (!isEmpty(embeddedPageId)) {
    storePageId = embeddedPageId;
  }
  const store = platform.getStore<GenericDataRecord>(
    appUid,
    storePageId,
    alias,
  );
  if (store === null) {
    throw Error(
      `Action Query: Missing store with alias '${alias}' in page '${appUid}.${storePageId}'!`,
    );
  }
  await store.initializeAttributes();
  const {
    aggregateList,
    data,
    fetchDistinct,
    filter,
    groupByAttributeCodeList,
    projection,
    sort,
  } = getFilterFromActions({
    appUid,
    platform,
    store,
    pageId,
    itemId,
    showEmptyFilterWarning,
    extraProps: _props as unknown as Record<string, unknown>,
  });

  const props: QueryProps<GenericDataRecord> = force ? { force } : {};

  if (aggregateList.length) {
    props.aggregateList = aggregateList;
  }
  if (filter.length) {
    props.filter = filter;
  }
  if (fetchDistinct) {
    props.fetchDistinct = fetchDistinct;
  }
  if (groupByAttributeCodeList.length) {
    props.groupByAttributeCodeList = groupByAttributeCodeList;
  }
  if (Object.keys(data).length) {
    props.data = data;
  }
  if (Object.keys(projection).length) {
    props.projection = projection;
  }
  if (Object.keys(sort).length) {
    props.sort = sort;
  }

  return [store, props];
}

async function Query(_props: ActionProps<'ActionQuery'>): Promise<boolean> {
  const { applyFiltersToSmartSearch } = _props;
  const [store, props] = await makeQueryProps(_props);

  if (applyFiltersToSmartSearch) {
    const attKeysInFilter = (props.filter ?? []).map((f) => getName(f));
    const storeFilters = store.getSmartSearchFilter().filter((f) => {
      const name = getName(f);
      return isNested(name) || !attKeysInFilter.includes(name);
    });
    const newFilters = [...storeFilters, ...(props.filter ?? [])];
    store.setSmartSeachFilter(newFilters);
    await store.query({});
  } else {
    await store.query({ props });
  }
  return true;
}

export default Query;
